import { TEMPLATE_CATEGORIES, type Template, type TemplateListItem } from '../types/templates.types';

/** Design `templates.html` content column: Ready | Empty (not Visual/Code). */
export const getTemplateContentStatus = (
  template: Pick<TemplateListItem, 'hasContent'> | Pick<Template, 'html'>
): 'Ready' | 'Empty' => {
  if ('hasContent' in template) return template.hasContent ? 'Ready' : 'Empty';
  return template.html?.trim() ? 'Ready' : 'Empty';
};

/** Whitespace plus the zero-width characters editors pad empty cells with. */
// An alternation, not a class: U+034F is a combining mark and would join its neighbour in a class.
const INVISIBLE_TEXT = /(?:\s|\u200B|\u200C|\u200D|\u2060|\uFEFF|\u034F|\u00AD)+/g;

/**
 * True when the email HTML has visible text, media with a `src`, or a web link — not just an empty
 * skeleton. Mirrors the API's `HtmlContent.HasMeaningfulContent`: the editor's `{{unsubscribeUrl}}`
 * footer is not the author's content, so it is set aside before checking.
 */
export const hasMeaningfulEmailContent = (html: string | null | undefined): boolean => {
  const source = html?.trim();
  if (!source) return false;
  if (typeof DOMParser === 'undefined') return true;

  const doc = new DOMParser().parseFromString(source, 'text/html');
  doc.querySelectorAll('style, script, head, title').forEach((node) => node.remove());
  doc.querySelectorAll('a[href*="{{unsubscribeUrl}}"]').forEach((node) => node.remove());
  if (doc.body.textContent?.replace(INVISIBLE_TEXT, '').length) return true;
  const hasMedia = Array.from(
    doc.body.querySelectorAll('img[src], video[src], audio[src], source[src], iframe[src]')
  ).some((node) => Boolean(node.getAttribute('src')?.trim()));
  if (hasMedia) return true;
  return Array.from(doc.body.querySelectorAll('a[href]')).some((node) =>
    /^https?:\/\//i.test(node.getAttribute('href')?.trim() ?? '')
  );
};

/** The canonical category, or `null` (uncategorized) for empty or legacy free-text values. */
export const resolveTemplateCategory = (
  category: string | null | undefined
): (typeof TEMPLATE_CATEGORIES)[number] | null => {
  const raw = category?.trim().toLowerCase();
  if (!raw) return null;
  return TEMPLATE_CATEGORIES.find((value) => value.toLowerCase() === raw) ?? null;
};

/** Only the five allowed categories have a label; anything else is Uncategorized. */
export const formatTemplateCategoryLabel = (category: string | null | undefined): string =>
  resolveTemplateCategory(category) ?? 'Uncategorized';

export const formatTemplateUpdatedDate = (updatedAt: string): string => {
  if (!updatedAt.trim()) return '—';

  const parsed = new Date(updatedAt);
  if (Number.isNaN(parsed.getTime())) return updatedAt;

  return parsed.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};
